<script setup>
import { defineProps, computed, watch, ref, defineEmits } from 'vue';
import EventsTable from '@/components/tables/EventsTable.vue';

const props = defineProps({
  widget: {
    type: Object,
    required: true
  },
  dataID: {
    type: String,
    required: true
  },
  timeFormat: {
    type: String,
    default: '24hour'
  },
  streamData: {
    type: Object,
    default: () => ({})
  },
  sourceErrors: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['update']);

// Stable template object - only recomputes when widget contents actually change,
// not on every parent re-render caused by unrelated streamData updates.
const eventTemplate = computed(() => ({ ...props.widget, type: 'event' }));

// The set of source names this events widget cares about
const widgetSources = computed(() => {
  if (!props.widget.values) return new Set();
  return new Set(props.widget.values.map(v => v.source).filter(Boolean));
});

const notFoundValueSources = computed(() => {
  const sources = (props.widget.values || []).map(v => v.source);
  return [...new Set(sources)].filter(s => s && props.sourceErrors?.[s]?.type === 'not_found');
});

const eventsTableRef = ref(null);

function handleTemplateUpdate(template) {
  // Merge template changes (columns, reports) back into widget shape and emit for persistence
  const updatedWidget = {
    ...props.widget,
    columns: template.columns,
    ...(template.reports != null && { reports: template.reports })
  };
  emit('update', updatedWidget);
}

watch(
  () => {
    // Build a snapshot of only the relevant sources' lastUpdate values.
    // The watcher only fires when one of these actually changes.
    const snapshot = {};
    for (const source of widgetSources.value) {
      snapshot[source] = props.streamData[source]?.lastUpdate ?? null;
    }
    return snapshot;
  },
  (newSnapshot, oldSnapshot) => {
    const hasNewData = Object.keys(newSnapshot).some(
      source => newSnapshot[source] && newSnapshot[source] !== oldSnapshot?.[source]
    );
    if (hasNewData && eventsTableRef.value) {
      eventsTableRef.value.refresh();
    }
  },
  { deep: true }
);
</script>

<template>
    <!-- missing source notice -->
    <div
      v-if="notFoundValueSources.length > 0"
      class="mb-2 flex flex-wrap gap-x-4 gap-y-1"
    >
      <span
        v-for="source in notFoundValueSources"
        :key="source"
        class="font-mono text-[11px] text-mc-red"
      >Source "{{ source }}" not found — some trigger variables may be missing.</span>
    </div>
    <EventsTable
    ref="eventsTableRef"
    :dataID="dataID"
    :template="eventTemplate"
    :eventJustCreated="widget._eventJustCreated"
    :canEdit="true"
    :canDelete="false"
    @update="handleTemplateUpdate"
  />
</template>
